/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.DataQueryService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods for retrieving data sources.
 */
DataQueryService.$inject = ['$q', 'mx.SolutionBuilderAgent.Http', 'mx.shell.DownloadHelpService'];
export function DataQueryService($q, http, downloadHelpService) {
	return {
		getFilters: getFilters,
		getFilter: getFilter,
		createFilter: createFilter,
		updateFilter: updateFilter,
		removeFilter: removeFilter,
		publishFilter: updateFilter,
		unpublishFilter: updateFilter,
		getRelationItems: getRelationItems,
		getRelationItemsWithTotalCount: getRelationItemsWithTotalCount,
		exportRelationItems: exportRelationItems,
		getRelationItemsCount: getRelationItemsCount,
		getMultiFragmentData: getMultiFragmentData,
		getMultiFragmentDataCount: getMultiFragmentDataCount,
		getGlobalSearchData: getGlobalSearchData,
		evaluateColumnExpression: evaluateColumnExpression,
		executeMultiFragmentExpressions: executeMultiFragmentExpressions,
	};

	function executeMultiFragmentExpressions(expressionRequest) {
		return http.post('api/dataQuery/executeMultiFragmentExpressions', expressionRequest);
	}

	function getGlobalSearchData(queryInfo) {
		return http.post('api/dataQuery/globalSearch/', queryInfo).then();
	}

	function getFilter(filterId) {
		return http.get(`api/dataQuery/filter/${filterId}`);
	}

	function getFilters(dataQueryId, widgetId) {
		return http.get(`api/dataQuery/${dataQueryId}/filters${widgetId ? `?widget=${widgetId}` : ''}`);
	}

	function createFilter(filter) {
		return http.post('api/dataQuery/filter/create', filter);
	}

	function updateFilter(filter) {
		return http.post('api/dataQuery/filter/update', filter);
	}

	function removeFilter(filterId) {
		return http.get(`api/dataQuery/filter/${filterId}/remove`);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DataQueryService#evaluateColumnExpression
	 *
	 * @description
	 * Evaluates the validity of the data expression
	 *
	 *
	 * @param {string} entityClass - Name of the Entity class.
	 * @param {string} expr - expression for evaluation.
	 * @param {guid} serviceOperationId - service operation Id.
	 *
	 * @returns {string} null if expression is invalid, or name of data type of the expression result
	 */
	function evaluateColumnExpression(entityClass, expr, serviceOperationId, isCondition, placeholders) {
		if (expr) {
			return http.get('api/dataQuery/evaluateExpression', {
				entityClass: entityClass,
				expr: expr,
				serviceOperationId: serviceOperationId,
				isCondition: isCondition,
				placeholders: placeholders,
			});
		}

		return $q.when(null);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DataQueryService#getRelationItems
	 *
	 * @description
	 * Gets relation items.
	 *
	 * @param {object} request - data, needed to get relation items.
	 *
	 * @returns {promise} - A list of relation items. <i>Resolves as an array of objects.</i>
	 */
	function getRelationItems(request) {
		const requestInfo = getRelationItemsRequest(request);

		return http.post('api/dataQuery/relationItems', requestInfo);
	}

	async function getRelationItemsWithTotalCount(request) {
		const requestInfo = getRelationItemsRequest(request);
		const response = await http.post('api/dataQuery/relationItems', requestInfo, {}, {}, false, true);
		return {
			items: response.data,
			totalCount: Number(response.headers('TotalCount') || ''),
		};
	}

	function exportRelationItems(request) {
		const requestInfo = getRelationItemsRequest(request);

		const headers = {
			responseType: 'arraybuffer',
		};

		return http.post('api/dataObject/exportObjectRelations', requestInfo, headers, {}, false, true).then(function (response) {
			downloadHelpService.downloadContent(response, response.data);
		});
	}

	function getRelationItemsRequest(request) {
		return Object.assign(
			{
				DataQueryName: request.dataQueryName,
				ObjectId: request.objectId || undefined,
				FragmentId: request.fragmentId,
				ClientTimeOffset: new Date().getTimezoneOffset(),
				EntityClassName: request.entityClassName,
				EntityTypeName: request.allowedTypes,
				RelationName: request.relationName,
				AddedRelations: listAsCsv(request.addedRelations),
				RemovedRelations: listAsCsv(request.removedRelations),
				UseObjectId: request.useObjectId, // means that AddedRelations and RemovedRelations use objectId instead of fragmentId
				AdditionalFilter: request.additionalFilter,
				Filters: request.filters,
				PageSize: request.pageSize,
				Page: request.pageNumber > 0 ? request.pageNumber - 1 : 0, // Todo: as backend use page-1
				Search: request.search || '',
				OrderBy: request.orderBy,
				Columns: request.columns,
				Archived: request.archived,
				LookupFor: request.lookupFor || '',
				ResetFilter: Boolean(request.resetFilter),
			},
			request.filter?.toRequest() || {}
		);
	}

	function getRelationItemsCount(request) {
		const requestInfo = getRelationItemsRequest(request);
		delete requestInfo.page;
		delete requestInfo.pageSize;
		delete requestInfo.orderBy;

		return http.post('api/dataQuery/relationItems/count', requestInfo);
	}

	function getMultiFragmentData(dataQueryId, objectId) {
		return dataQueryId && objectId ? http.get(`api/dataQuery/${dataQueryId}/${objectId}`) : $q.reject('wrong attributes');
	}

	function getMultiFragmentDataCount(dataQueryId, objectId) {
		return dataQueryId && objectId ? http.get(`api/dataQuery/${dataQueryId}/${objectId}/count`) : $q.reject('wrong attributes');
	}

	function listAsCsv(list) {
		if (!list) {
			return '';
		}
		return Array.isArray(list) ? list.join() : list;
	}
}
